import React from "react";
import { formatMoney } from "../../../../features/utils/formatMoney";
import {
  getChangeAdminGuidance,
  getChangeKindLabel,
  hasChangeQuote,
  isChangePaymentSecured,
  isOpenTicketChange,
} from "../../../../data/flight/order/getChangeAdminGuidance";
import { getRequestedDateChangeSummary } from "../../../../data/flight/order/getRequestedDateChange";

function money(amount, currency) {
  if (amount == null || amount === "") return "—";
  const formatted = formatMoney(Number(amount) || 0);
  return currency ? `${formatted} ${currency}` : formatted;
}

function paymentLabel(data) {
  const pending = Array.isArray(data?.bankTransferProofs)
    ? data.bankTransferProofs.some(
        (p) => String(p?.status || "").toLowerCase() === "pending",
      )
    : false;
  if (pending) return "Bank transfer pending approval";
  if (isChangePaymentSecured(data)) {
    return data?.paymentStatus || data?.status || "Payment authorised";
  }
  const payment = String(data?.paymentStatus || "").trim();
  if (payment) return payment;
  const status = String(data?.status || "").toLowerCase();
  if (
    status.includes("accepted") ||
    status === "paymentrequired" ||
    status === "datechangequoted" ||
    status === "namechangequoted"
  ) {
    return data?.status;
  }
  if (Number(data?.totalAmountPayable || data?.payable || 0) <= 0) {
    return "No payment due";
  }
  return "Awaiting payment";
}

function itinerarySummary(data) {
  const summary = getRequestedDateChangeSummary(data);
  if (summary) return summary;
  const selected = Array.isArray(data?.selectedSegment)
    ? data.selectedSegment
    : [];
  if (selected.length) {
    return selected
      .map((seg) => {
        const from =
          seg?.departureLocation || seg?.departure?.location || seg?.from;
        const to = seg?.arrivalLocation || seg?.arrival?.location || seg?.to;
        const date = seg?.departureDate || seg?.departure?.date || "";
        return `${from || "?"} → ${to || "?"} · ${date}`;
      })
      .join("; ");
  }
  return "See flight details";
}

/** Task 6 — accepted itinerary, amount, payment before airline work */
export default function ChangeAcceptedProcessCard({ data }) {
  const guidance = getChangeAdminGuidance(data);
  if (isOpenTicketChange(data)) return null;

  const show = [
    "needs_airline_submit",
    "send_failed",
    "awaiting_supplier",
    "needs_reissue",
    "settlement_failed",
    "failed",
    "supplier_failed",
  ].includes(guidance.stage);

  if (!show && !(isChangePaymentSecured(data) && hasChangeQuote(data))) {
    return null;
  }

  const kind = getChangeKindLabel(data);
  const currency =
    data?.currency ||
    data?.flightBooking?.[0]?.currency ||
    data?.flightBooking?.currency ||
    "";
  const payable = Number(data?.totalAmountPayable || data?.payable || 0);

  return (
    <div className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white mt-4 p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="text-base font-bold text-gray-900">
          Process this {kind}
        </h2>
        <span className="text-xs font-semibold px-2 py-1 rounded-md bg-[#1E61DC]/10 text-[#1E61DC]">
          {guidance.statusLabel}
        </span>
      </div>
      <p className="text-xs text-gray-600 mb-3">
        Agent accepted. Check itinerary, amount, and payment, then work with the
        airline. Do not create a new quote on this same request.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div className="rounded-lg border border-[#E8EEF5] bg-[#F8FAFC] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
            Accepted itinerary
          </div>
          <div className="mt-1 text-[#0F172A] font-medium leading-snug">
            {itinerarySummary(data)}
          </div>
        </div>
        <div className="rounded-lg border border-[#E8EEF5] bg-[#F8FAFC] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
            Accepted amount
          </div>
          <div className="mt-1 text-[#0F172A] font-semibold">
            {money(payable, currency)}
          </div>
        </div>
        <div className="rounded-lg border border-[#E8EEF5] bg-[#F8FAFC] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
            Payment status
          </div>
          <div className="mt-1 text-[#0F172A] font-semibold">
            {paymentLabel(data)}
          </div>
        </div>
      </div>
    </div>
  );
}
